import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, useState, type JSX } from 'react'
import { toast } from 'sonner'

import { useMerchants } from '../hooks/use-merchants.ts'
import { Button } from '../design-system/button.tsx'
import { Dialog } from '../design-system/dialog.tsx'
import { Panel } from '../design-system/panel.tsx'
import {
  ALERT_RULES_QUERY_KEY,
  createAlert,
  previewAlert,
  updateAlert,
  type AlertReport,
  type AlertRule,
  type AlertView,
  type NewAlert
} from '../lib/alert-rules-api.ts'
import {
  choiceEmails,
  choicePostsToChannel,
  draftFromRule,
  labelProblem,
  newAlertFromDraft,
  newDraft,
  viewSearch,
  triggerFromDraft,
  type AlertDraft
} from '../lib/alert-draft.ts'
import {
  searchFromSavedView,
  selectedView,
  startingPeriod,
  type AlertSearch
} from '../lib/alert-view.ts'
import { readEmailTo } from '../lib/alert-email-to.ts'
import { readSlackChannel } from '../lib/alert-slack-channel.ts'
import { pagePeriod } from '../lib/page-period.ts'
import {
  ScheduleStep,
  TriggerStep,
  WatchStep,
  type SetField
} from './alert-sheet-steps.tsx'
import {
  AlertProblems,
  PreviewActions,
  PreviewStep
} from './alert-sheet-preview.tsx'

/** The four steps, in the order the sheet walks them. */
const STEPS = [
  'What to watch',
  'When to tell you',
  'How often, and how much',
  'Preview'
] as const

/** The last step's index. */
const PREVIEW_STEP = STEPS.length - 1

/**
 * AlertSheet — the sheet that builds a saved-view alert from the report on
 * screen, or edits one already saved.
 *
 * `AlertMe` finds the report in the catalogue and opens this. My alerts opens
 * it on a saved alert, with every choice that alert made already set; Save
 * then changes that alert rather than adding one. The sheet reads
 * the view from the URL itself, so no page hands it anything. Four steps:
 * what to watch, when to tell you, how often and how much, then a preview of
 * the DM. Save stores it and My alerts picks it up.
 *
 * @param props - Sheet props.
 * @param props.report - The catalogue entry for this page.
 * @param props.open - Whether the sheet is open.
 * @param props.onOpenChange - Opens or shuts the sheet.
 * @param props.savedView - A saved view to start from. Absent, the sheet reads the page URL.
 * @param props.rule - A saved alert to edit. It wins over `savedView`.
 * @returns The sheet.
 */
export function AlertSheet({
  report,
  open,
  onOpenChange,
  savedView = null,
  rule = null
}: {
  report: AlertReport
  open: boolean
  onOpenChange: (open: boolean) => void
  savedView?: { view: AlertView; label: string } | null
  rule?: AlertRule | null
}): JSX.Element {
  const title = rule ? 'Edit alert' : 'Alert me'
  let description =
    'A Slack DM, a Slack channel post or an email, from the view on this page.'
  if (rule) {
    description =
      'Change what it watches, when it fires, how often and where it goes. A new view, trigger or schedule starts the alert afresh.'
  } else if (savedView) {
    description =
      'A Slack DM, a Slack channel post or an email, from this saved view.'
  }

  return (
    <Dialog
      data-testid="alert-sheet"
      title={title}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Panel
        data-testid="alert-sheet-panel"
        title={title}
        description={description}
      >
        {/* Mounted each time the sheet opens, so a second opening starts
            again from the page rather than from a half-filled draft. */}
        {open && rule ? (
          <AlertForm
            report={report}
            onClose={() => onOpenChange(false)}
            search={searchFromSavedView(rule.source.view)}
            pageDefault={null}
            label={rule.label ?? ''}
            rule={rule}
          />
        ) : null}
        {open && !rule ? (
          savedView ? (
            <AlertForm
              report={report}
              onClose={() => onOpenChange(false)}
              search={searchFromSavedView(savedView.view)}
              pageDefault={null}
              label={savedView.label}
            />
          ) : (
            <PageAlertForm
              report={report}
              onClose={() => onOpenChange(false)}
            />
          )
        ) : null}
      </Panel>
    </Dialog>
  )
}

/**
 * The form for the page on screen. The router hook stays here so a saved
 * view, which already has its search, does not read this page's address.
 *
 * @param props - Form props.
 * @param props.report - The catalogue entry.
 * @param props.onClose - Shuts the sheet after a save.
 * @returns The form.
 */
function PageAlertForm({
  report,
  onClose
}: {
  report: AlertReport
  onClose: () => void
}): JSX.Element {
  const search = useRouterState({ select: state => state.location.search })

  return (
    <AlertForm
      report={report}
      onClose={onClose}
      search={search}
      pageDefault={pagePeriod()}
      label=""
    />
  )
}

/**
 * The four steps.
 *
 * @param props - Form props.
 * @param props.report - The catalogue entry.
 * @param props.onClose - Shuts the sheet after a save.
 * @param props.search - The page search the view is read from.
 * @param props.pageDefault - The period the page shows, or null for a saved view.
 * @param props.label - The name the label field starts with.
 * @param props.rule - The saved alert Save changes. Absent, Save adds one.
 * @returns The step on screen.
 */
function AlertForm({
  report,
  onClose,
  search,
  pageDefault,
  label,
  rule = null
}: {
  report: AlertReport
  onClose: () => void
  search: AlertSearch
  pageDefault: string | null
  label: string
  rule?: AlertRule | null
}): JSX.Element {
  const queryClient = useQueryClient()
  const directory = useMerchants()
  const autoPreviewed = useRef(false)
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<AlertDraft>(() =>
    rule
      ? draftFromRule(rule, report)
      : {
          ...newDraft(
            report,
            startingPeriod(search, report.periods, pageDefault)
          ),
          label
        }
  )
  const setField: SetField = (key, value) =>
    setDraft(current => ({ ...current, [key]: value }))
  const built = newAlertFromDraft(draft, report, search)
  const nextDisabled =
    (step === 0 &&
      selectedView(
        viewSearch(search, draft),
        report,
        draft.period,
        draft.dropped
      ) === null) ||
    (step === 1 && triggerFromDraft(draft, report) === null) ||
    (step === 2 &&
      (labelProblem(draft.label) !== null ||
        (choiceEmails(draft.delivery) && !readEmailTo(draft.emailTo).ok) ||
        (choicePostsToChannel(draft.delivery) &&
          !readSlackChannel(draft.slackChannel).ok)))
  const preview = useMutation({
    mutationFn: (body: NewAlert & { send?: boolean }) => previewAlert(body)
  })
  useEffect(() => {
    if (built && !autoPreviewed.current) {
      autoPreviewed.current = true
      preview.mutate(built)
    }
  }, [built, preview])
  const save = useMutation({
    // An empty `emailTo` sends to the owner again, so a cleared list is sent, not left out.
    mutationFn: (alert: NewAlert) =>
      rule
        ? updateAlert({ ...alert, id: rule.id, emailTo: alert.emailTo ?? [] })
        : createAlert(alert),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ALERT_RULES_QUERY_KEY })
      toast.success(rule ? 'Alert updated' : 'Alert saved')
      onClose()
    }
  })
  // The first preview runs as the sheet opens, before the reader sets the
  // filters, the trigger or where it goes. Any change previews again on
  // reaching the step, so the preview never shows a view the alert will not watch.
  const next = () => {
    const { send: _send, ...previewed } = preview.variables ?? {}
    if (
      step + 1 === PREVIEW_STEP &&
      built &&
      JSON.stringify(previewed) !== JSON.stringify(built)
    ) {
      preview.mutate(built)
    }
    setStep(current => current + 1)
  }
  const failure = save.error ?? preview.error
  const stepProps = { report, draft, setField }

  return (
    <div className="grid gap-4">
      <h2 data-testid="alert-sheet-step" className="type-section-title">
        {STEPS[step]}
      </h2>

      {step === 0 ? (
        <WatchStep
          {...stepProps}
          search={viewSearch(search, draft)}
          merchants={directory.data?.merchants ?? []}
        />
      ) : null}
      {step === 1 ? <TriggerStep {...stepProps} /> : null}
      {step === 2 ? <ScheduleStep {...stepProps} /> : null}
      {step === PREVIEW_STEP ? (
        <PreviewStep
          preview={preview.data}
          delivery={preview.variables?.delivery ?? ['slack']}
        />
      ) : null}

      {failure ? <AlertProblems error={failure} /> : null}

      <div className="flex flex-wrap gap-2">
        {step > 0 ? (
          <Button
            data-testid="alert-sheet-back"
            variant="outline"
            onClick={() => setStep(current => current - 1)}
          >
            Back
          </Button>
        ) : null}
        {step < PREVIEW_STEP ? (
          <Button
            data-testid="alert-sheet-next"
            disabled={nextDisabled}
            onClick={next}
          >
            Next
          </Button>
        ) : null}
        {step === PREVIEW_STEP && built ? (
          <PreviewActions alert={built} preview={preview} save={save} />
        ) : null}
      </div>
    </div>
  )
}
